import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import Api from './SMPFetchHelper';
import config from '../conf/config';
import {getAxiosResponseErrorCode, isStringI18n, extractI18nString, getFilterFreeTextSearchValue} from '../utils/utils';
import {SortDirections, ApiErrorCodes} from './consts';
import {useTranslation} from 'react-i18next';

export function useGenericResultsDataApi(
    objectType,
    defaultSortBasis,
    defaultSortDirection,
    defaultFilter
) {
    const { t } = useTranslation();

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => isMountedRef.current = false;
    }, []);

    /**
     * Fetch items from API and return them with a promise
     * @param {Number} startIndex index to fetch from
     * @param {Number} maxItems maximum items to fetch
     * @param {String} sort the basis string to sort with
     * @param {('asc'|'desc')} sortDirection ascencind or descending
     * @param {Object} filterObject filter object to pass to server
     */
    const fetchItems = useCallback(async (startIndex, maxItems, sort, sortDirection, filterObject) => {
        return new Promise(async (resolve, reject) => {
            try {
                const res = await Api.fetchObjectList(objectType, startIndex, maxItems, sort, sortDirection, filterObject);
                const resultData = res.data;
                let dataObjectsFromServer = resultData['data'];
                const nextBaseIndex = resultData['nextBaseIndex'];
                const isLastPage = resultData['fetchedAll'] === true;
                // Save dataObjectsFromServer as an array even if empty
                if (!dataObjectsFromServer) {
                    dataObjectsFromServer = [];
                }
                resolve({dataObjectsFromServer, nextBaseIndex, isLastPage});
            } catch(err) {
                reject(err);
            }
        })
    }, [objectType]);
    
    const [sortBasis, setSortBasis] = useState(defaultSortBasis);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [filterObject, setFilterObject] = useState(defaultFilter || {});
    
    // Results data
    const [itemsFetched, setItemsFetched] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [tableContent, setTableContent] = useState([]);
    const [selectedObjects, setSelectedObjects] = useState([]);

    // Referance to is fetching
    const isFetchingRef = useRef(false);
    // Keep a reference to the last fetch promise, so we can ignore all old responses
    const lastFetchPromise = useRef();
    // Hold a ref of isGiveupFetching, se we can giveup retrying after failed attempts
    const isGiveupFetchingRef = useRef(false);

    const clearSelectedObjects = useCallback(() => {
        setSelectedObjects([]);
    }, []);

    // Watch isFetching value
    useEffect(() => {
        isFetchingRef.current = isFetching;
    }, [isFetching]);

    const fetchNextPage = useCallback(async () => {
        return new Promise(async (resolve, reject) => {
            // If is fetching, or is last page, or if given up, dont try to fetch
            if (!isFetchingRef.current && !isLastPage && !isGiveupFetchingRef.current) {
                try {
                    setIsFetching(true);
                    const currentPromise = fetchItems(itemsFetched, config.results_page_size, sortBasis, sortDirection, filterObject);
                    // Save as last promise
                    lastFetchPromise.current = currentPromise;
                    const { dataObjectsFromServer, nextBaseIndex, isLastPage } = await currentPromise;
                    // Only set the state if this is the newest fetch command
                    if (lastFetchPromise.current === currentPromise) {
                        setTableContent(data => [...data, ...dataObjectsFromServer]);
                        setItemsFetched(nextBaseIndex);
                        setIsLastPage(isLastPage);
                    }
                    setIsFetching(false);
                } catch (e) {
                    setIsFetching(false);
                    isGiveupFetchingRef.current = true;
                    const errorCode = getAxiosResponseErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
                    reject(errorCode);
                }
                setIsFetching(false);
            }
            resolve();
        });
    }, [isLastPage, fetchItems, itemsFetched, sortBasis, sortDirection, filterObject]);
    
    const reloadTable = useCallback((clearSelected=true) => {
        return new Promise(async (resolve, reject) => {
            try {
                setIsFetching(true);
                setTableContent([]);
                const currentPromise = fetchItems(0, config.results_page_size, sortBasis, sortDirection, filterObject);
                // Save as last promise
                lastFetchPromise.current = currentPromise;
                const {dataObjectsFromServer, nextBaseIndex, isLastPage} = await currentPromise;
                // Only set the state if this is the newest fetch command, and component still mounted
                if (lastFetchPromise.current === currentPromise && isMountedRef.current) {
                    clearSelected && clearSelectedObjects();
                    setItemsFetched(nextBaseIndex);
                    setIsLastPage(isLastPage);
                    setTableContent(dataObjectsFromServer);
                    // Dev note: Use id instead of react ref to avoid unnecessary complexity
                    if (document.getElementById('ResultsTableDiv')) {
                        document.getElementById('ResultsTableDiv').parentElement.scrollTo(0,0); // Scroll results table to top
                    }
                    setIsFetching(false);
                }
            } catch (e) {
                setIsFetching(false);
                const errorCode = getAxiosResponseErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
                return reject(errorCode);
            }
            return resolve('RESULTS_OBJS_REFRESHED_OK');
        });
    }, [fetchItems, sortBasis, sortDirection, clearSelectedObjects, filterObject]);

    const reloadTableNoClearSelected = useCallback(() => {
        return reloadTable(false);
    }, [reloadTable]);

    const setSort = useCallback(newSortBasis => {
        if (newSortBasis === sortBasis) {
            // If same sort name selected, toggle sort direction
            if (sortDirection === SortDirections.ASCENDING) {
                setSortDirection(SortDirections.DESCENDING);
            } else {
                setSortDirection(SortDirections.ASCENDING);
            }
        } else {
            setSortBasis(newSortBasis);
            setSortDirection(SortDirections.ASCENDING); // Default sort direction is ascending
        }
    }, [sortBasis, sortDirection]);

    // // Reload table on sortBasis and sortDirection, as well as first time loading
    // useEffect(() => {
    //     reloadTable((successCode, errorCode) => { setErrorMessage(t(errorCode)) });
    // }, [sortBasis, sortDirection, reloadTable, t]);

    const deleteObjects = useCallback((objects, password, timeout) => {
        return new Promise(async (resolve, reject) => {
            if (!objects) {
                return reject('No objects selected');
            }
            const objectsList = Array.isArray(objects) ? objects : [objects];
            try {
                const res = await Api.deleteObjects(objectType, objectsList, password, timeout);
                if (res.data.results) {
                    const failedToDelete = res.data.results.filter(elem => !elem.success);
                    const successfullyDeleted = res.data.results.filter(elem => elem.success);
                    let successMessage;
                    let errorMessage;
                    if (failedToDelete.length) {
                        errorMessage = (
                        <>
                            {t('RESULTS_DELETE_ERROR_OCCURED', [failedToDelete.length])}
                            {
                                failedToDelete.map(failedRes => {
                                    const errorParams = failedRes.errorParams ? failedRes.errorParams.map(param => isStringI18n(param) ? t(extractI18nString(param)) : param) : undefined;
                                    return (
                                        <>
                                        <br />
                                        {t(extractI18nString(failedRes.errorCode), errorParams)}
                                        </>
                                    )
                                })
                            }
                        </>
                        )
                    }
                    if (successfullyDeleted.length) {
                        successMessage = t('RESULTS_OBJS_DELETED_OK', [successfullyDeleted.length]);
                    }
                    return resolve({ successMessage, errorMessage });
                }
            } catch (e) {
                console.error('Error deleting objects ' + e.message);
                return reject(e);
            }
        });
    }, [objectType, t]);

    const deleteSelectedObjects = useCallback((password, timeout) => {
        return new Promise(async (resolve, reject) => {
            try {
                resolve(deleteObjects(selectedObjects, password, timeout));
            } catch (e) {
                reject(e);
            }
        });
    }, [deleteObjects, selectedObjects]);

    const freeTextSearch = useMemo(() => {
        return getFilterFreeTextSearchValue(filterObject);
    }, [filterObject]);

    return (
        {
            tableContent,
            isFetching,
            setSort,
            sortBasis,
            sortDirection,
            fetchNextPage,
            isLastPage,
            reloadTable,
            reloadTableNoClearSelected,
            selectedObjects,
            setSelectedObjects,
            deleteSelectedObjects,
            deleteObjects,
            filterObject,
            setFilterObject,
            freeTextSearch
        }
    );
}